/**
 * useClickOutside — close-on-outside-interaction for popovers, dropdowns, menus.
 *
 * Replaces the hand-rolled
 *   useEffect(() => {
 *     const h = (e) => { if (ref.current && !ref.current.contains(e.target)) close(); };
 *     document.addEventListener('mousedown', h);
 *     return () => document.removeEventListener('mousedown', h);
 *   }, [...])
 * block that was duplicated across ~13 components. Attaches a single `mousedown`
 * listener (and optionally a `keydown` Escape listener) while `enabled`, and calls
 * `handler` when the interaction lands outside ALL of the supplied refs.
 */

import { useEffect, type RefObject } from 'react';

type ElementRef = RefObject<HTMLElement | null>;

export interface UseClickOutsideOptions {
  /**
   * Only listen while true. Pass the open/visible flag to mirror the common
   * `if (isOpen) document.addEventListener('mousedown', ...)` guard. Default: true.
   */
  enabled?: boolean;
  /** Also invoke `handler` when the Escape key is pressed. Default: false. */
  escape?: boolean;
  /**
   * Predicate to suppress specific pointer events — return true to ignore them.
   * Used by ProviderPicker to keep the panel open when the user drags a native
   * scrollbar gutter (which reports an outside element as the event target).
   */
  ignore?: (event: MouseEvent) => boolean;
}

/**
 * @param refs    A single ref, or an array of refs, defining the "inside" region.
 *                A pointer-down inside any of them does NOT trigger `handler`.
 * @param handler Called on a qualifying outside interaction (or Escape). Usually closes the element.
 * @param options See {@link UseClickOutsideOptions}.
 */
export function useClickOutside(
  refs: ElementRef | ElementRef[],
  handler: () => void,
  options: UseClickOutsideOptions = {}
): void {
  const { enabled = true, escape = false, ignore } = options;

  useEffect(() => {
    if (!enabled) return;

    const refList = Array.isArray(refs) ? refs : [refs];

    const handlePointerDown = (event: MouseEvent): void => {
      const target = event.target as Node | null;
      if (!target) return;
      if (refList.some((ref) => ref.current?.contains(target))) return;
      if (ignore?.(event)) return;
      handler();
    };

    document.addEventListener('mousedown', handlePointerDown);

    let handleKeyDown: ((event: KeyboardEvent) => void) | undefined;
    if (escape) {
      handleKeyDown = (event: KeyboardEvent): void => {
        if (event.key === 'Escape') handler();
      };
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      if (handleKeyDown) document.removeEventListener('keydown', handleKeyDown);
    };
  }, [refs, handler, enabled, escape, ignore]);
}
